import { toRaw } from 'vue'
import type {
  BarcodeScannedEventData,
  DomEventData,
  PrepareBetslipEvent,
  PrepareCancelBetEvent,
  BetEventsFeBetslipIdData,
  IntegratorParentPOSConfig,
  POSSetWidgetVisibilityEvent,
  POSSetLayoutModeEvent,
} from '@nsftx/seven-client-retail-vue-integrator'
import {
  handleDocumentOpen,
  handleNotificationRequired,
  handleShortcutKeydown,
  useNotificationsStore,
} from '@nsftx/seven-nxg-retail-vue-sdk'
import { NXGDSNotificationVariants } from '@nsftx/seven-nxg-design-system'
import i18n from '@/plugins/translationsPlugin'
import {
  useDeviceManagementPrinterStatusStore,
  useWorkingTimeStore,
} from '@nsftx/seven-client-retail-vue-dm'
import { isVoucher, logService } from '@nsftx/seven-client-retail-utils'
import { useGamesStore, useTicketsStore } from '@nsftx/seven-client-retail-vue-sgp'
import errorParser from '@/utils/errorParser'
import slipCheckerService from '../slip-checker/slipCheckerService'
import { handleSlipPayinSuccess, handleSlipPayinError } from '../slip-payin/slipPayinService'
import { useWidgetsStore } from '@/stores/widgets/widgetsStore'
import { useGameConfigurationStore } from '@/stores/cms/gameConfigurationStore'
import { resolveProductLinkCode } from './integratorProductUtils'
import { checkAndShowVoucherDetails } from '../voucher/voucherPOSService'
import { checkAndShowSlipDetails } from '../slip-check/slipCheckService'
import { useVoucherPOSStore } from '@/stores/vouchers/voucherPOSStore'
import { useSlipCheckStore } from '@/stores/slip-check/slipCheckStore'
const LOG_PREFIX = '[integratorPOSEventsService]'
const { t } = i18n.global

const showLocationWorkingHoursOffNotification = (nextValidTime?: string) => {
  const { showNotification } = useNotificationsStore()

  showNotification({
    variant: NXGDSNotificationVariants.warning,
    text: [
      t('working_hours_off', {
        datetime: nextValidTime || t('common_unknown'),
      }),
    ],
  })
}

const resolveEventProductLinkCode = (childId: string, sourceProduct?: { linkCode: string }) => {
  const { gameLinks } = useGamesStore()
  const { getByGameId } = useGameConfigurationStore()

  return resolveProductLinkCode({
    childId,
    sourceProduct,
    gameLinks,
    getCmsConfig: getByGameId,
  })
}

const onLoaded = () => {
  logService.info(`${LOG_PREFIX} Product loaded`, {
    code: 'SS_PRODUCT_LOADED',
  })
}

const onPrepareBetslip = (event: PrepareBetslipEvent) => {
  const { isPrinterValid, printerStatus } = useDeviceManagementPrinterStatusStore()
  const { addPendingTicket } = useTicketsStore()
  const { feBetslipId, bets, sourceProduct } = event.data
  const childId = event.childId
  const linkCode = resolveEventProductLinkCode(childId, sourceProduct)

  logService.debug(`${LOG_PREFIX} onPrepareBetslip callback called.`, {
    code: 'SS_PREPARE_BETSLIP_EVENT_SUCCESS',
    feBetslipId,
    linkCode,
    details: {
      childId,
      sourceProduct,
      uuid: event.uuid,
    },
  })

  if (!isPrinterValid()) {
    logService.error(`${LOG_PREFIX} Printer status is not valid.`, {
      code: 'SS_PREPARE_BETSLIP_PRINTER_STATUS_ERROR',
      feBetslipId,
      linkCode,
      details: {
        printerStatus: toRaw(printerStatus),
        childId,
        sourceProduct,
      },
    })

    return event.reject({
      status: 'NOK',
      code: 'SS_PREPARE_BETSLIP_PRINTER_STATUS_ERROR',
      data: {
        feBetslipId,
        bets,
      },
    })
  }

  const workingTimeStore = useWorkingTimeStore()
  const { isValid, nextValidTime } = workingTimeStore.isWorkingTimeValid()

  if (!isValid) {
    showLocationWorkingHoursOffNotification(nextValidTime)

    logService.error(`${LOG_PREFIX} Working time is over.`, {
      code: 'SS_PREPARE_BETSLIP_WORKING_TIME_ERROR',
      feBetslipId,
      linkCode,
      details: {
        childId,
        sourceProduct,
      },
    })

    return event.reject({
      code: 'SS_PREPARE_BETSLIP_WORKING_TIME_ERROR',
      status: 'NOK',
      data: {
        feBetslipId,
        bets,
      },
    })
  }

  const { getGameLinkByCode } = useGamesStore()
  const gameLink = linkCode ? getGameLinkByCode(linkCode) : undefined

  if (!gameLink) {
    logService.error(`${LOG_PREFIX} Game link not found`, {
      code: 'SS_PREPARE_BETSLIP_GAME_LINK_NOT_FOUND',
      feBetslipId,
      linkCode,
      details: {
        childId,
        sourceProduct,
      },
    })

    return event.reject({
      code: 'SS_PREPARE_BETSLIP_GAME_LINK_NOT_FOUND',
      status: 'NOK',
      data: {
        feBetslipId,
        bets,
      },
    })
  }

  addPendingTicket(feBetslipId, gameLink)
  slipCheckerService.handleSlipChecker({
    activeFeBetslipId: feBetslipId,
    onResolveSuccess: handleSlipPayinSuccess,
    onResolveFailed: handleSlipPayinError,
  })

  return event.resolve({
    status: 'OK',
    code: '',
    data: {
      feBetslipId,
      bets,
    },
  })
}

const onFailBetslip = (eventData: BetEventsFeBetslipIdData) => {
  const { setFailedTicket } = useTicketsStore()
  const { feBetslipId, sourceProduct } = eventData
  const linkCode = sourceProduct?.linkCode

  logService.debug(`${LOG_PREFIX} onFailBetslip callback called.`, {
    code: 'SS_FAIL_BETSLIP_EVENT_SUCCESS',
    feBetslipId,
    linkCode,
    details: {
      sourceProduct,
    },
  })

  try {
    setFailedTicket(feBetslipId)
  } catch (error) {
    logService.warn(`${LOG_PREFIX} Could not set ticket to failed.`, {
      ...errorParser.parseUpstream(error),
      feBetslipId,
      linkCode,
    })
  }
}

const onPrepareCancelBet = (event: PrepareCancelBetEvent) => {
  const workingTimeStore = useWorkingTimeStore()
  const { isValid, nextValidTime } = workingTimeStore.isWorkingTimeValid()
  const { feBetslipId, sourceProduct } = event.data
  const linkCode = resolveEventProductLinkCode(event.childId, sourceProduct)

  logService.debug(`${LOG_PREFIX} onPrepareCancelBet callback called.`, {
    code: 'SS_PREPARE_CANCEL_BET_EVENT_SUCCESS',
    feBetslipId,
    linkCode,
    details: {
      uuid: event.uuid,
      childId: event.childId,
      sourceProduct,
    },
  })

  if (!isValid) {
    showLocationWorkingHoursOffNotification(nextValidTime)

    logService.error(`${LOG_PREFIX} Working time is over.`, {
      code: 'SS_PREPARE_CANCEL_BET_WORKING_TIME_ERROR',
      feBetslipId,
      linkCode,
      details: {
        id: 'working-time-error',
        message: 'Working time is over.',
        code: 'SS_PREPARE_CANCEL_BET_WORKING_TIME_ERROR',
        sourceProduct,
      },
    })

    return event.reject({
      code: 'SS_PREPARE_CANCEL_BET_WORKING_TIME_ERROR',
      status: 'NOK',
      data: {
        feBetslipId,
        bets: [],
      },
    })
  }

  return event.resolve({
    code: '',
    status: 'OK',
    data: {
      feBetslipId,
      bets: [],
    },
  })
}

const onFailCancelBet = (eventData: BetEventsFeBetslipIdData) => {
  const { feBetslipId, sourceProduct } = eventData
  const linkCode = sourceProduct?.linkCode

  logService.debug(`${LOG_PREFIX} onFailCancelBet callback called.`, {
    code: 'SS_FAIL_CANCEL_BET_EVENT_SUCCESS',
    feBetslipId,
    linkCode,
    details: {
      sourceProduct,
    },
  })
}

const onDomEvent = (data: DomEventData) => {
  logService.debug(`${LOG_PREFIX} onDomEvent callback called.`, {
    code: 'SS_DOM_EVENT_SUCCESS',
    details: {
      type: data.type,
      params: data.params,
      sourceProduct: data.sourceProduct,
    },
  })

  switch (data.type) {
    case 'click':
      document.body.click()
      break
    case 'keydown':
      handleShortcutKeydown(data.params as KeyboardEvent)
      break
    default:
      document.body.click()
  }
}

const onBarcodeScanned = (scanEventData: BarcodeScannedEventData) => {
  const code = scanEventData.data.code
  const { closeVoucherDetailsModal } = useVoucherPOSStore()
  const { closeSlipCheckDetailsModal } = useSlipCheckStore()

  logService.debug(`${LOG_PREFIX} onBarcodeScanned callback called.`, {
    code: 'SS_BARCODE_SCAN_EVENT_SUCCESS',
    details: {
      code,
    },
  })

  closeVoucherDetailsModal()
  closeSlipCheckDetailsModal()

  if (isVoucher(code)) {
    checkAndShowVoucherDetails(code)
  } else {
    checkAndShowSlipDetails(code)
  }
}

const onWidgetVisibilitySet = (event: POSSetWidgetVisibilityEvent) => {
  const { setWidgetVisibility } = useWidgetsStore()

  logService.debug(`${LOG_PREFIX} Widget visibility set.`, {
    code: 'SS_WIDGET_VISIBILITY_SET_SUCCESS',
    details: {
      ...event.data,
    },
  })

  setWidgetVisibility(event.data)

  event.resolve(true)
}

const onLayoutModeSet = (event: POSSetLayoutModeEvent) => {
  const { setLayoutMode } = useWidgetsStore()
  const { mode } = event.data

  logService.debug(`${LOG_PREFIX} Layout mode set.`, {
    code: 'SS_LAYOUT_MODE_SET_SUCCESS',
    details: {
      newActiveLayoutMode: mode,
    },
  })

  setLayoutMode(mode)

  event.resolve(true)
}

export const integratorPOSEvents: NonNullable<IntegratorParentPOSConfig['events']> = {
  onLoaded,
  onPrepareBetslip,
  onFailBetslip,
  onPrepareCancelBet,
  onFailCancelBet,
  onDomEvent,
  onBarcodeScanned,
  onWidgetVisibilitySet,
  onLayoutModeSet,
  onDocumentOpen: handleDocumentOpen,
  onNotificationRequired: handleNotificationRequired,
}
